import { rem, stripUnit } from 'polished'
import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  type SpaceProps,
  type FontFamilyProps,
  type FontSizeProps,
} from 'styled-system'
import { Box, Flex } from 'components/Layout'
import { GRADIENT, SPACE } from 'Theme'

const DEFAULT_TEXT_HEIGHT = rem(20)
const GRADIENT_HEIGHT = rem(40)
const TRANSITION_TIME = '250ms'

const Wrapper = styled(Box)`
  overflow: hidden;
  transition: ${TRANSITION_TIME} ease-in-out;
`
const Gradient = styled(Box)`
  background: linear-gradient(${GRADIENT.GRADIENT_3});
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`

const ActionButtonWrapper = styled(Box)`
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`
const convertHeightToRem = (height) => {
  if (typeof height !== 'string') {
    return stripUnit(rem(height))
  }
  if (height.includes('px')) {
    return stripUnit(rem(stripUnit(height)))
  }
  return stripUnit(height)
}

interface ReadMoreProps extends FontFamilyProps, FontSizeProps {
  collapsedHeight?: string
  isExpanded: boolean
  actionButton: () => React.ReactNode
  fitParentHeight?: boolean
  wrapperRef?: React.MutableRefObject<HTMLDivElement>
  actionButtonWrapperProps?: SpaceProps
}

export const ReadMore: React.FC<React.PWC<ReadMoreProps>> = ({
  children,
  isExpanded,
  actionButton,
  wrapperRef,
  collapsedHeight = DEFAULT_TEXT_HEIGHT,
  fitParentHeight = false,
  actionButtonWrapperProps = { mt: SPACE.PX_5 },
  ...props
}) => {
  const textWrapperRef = useRef(null)
  const boxRef = useRef(null)
  const initialLoad = useRef(true)
  const [isTextLong, setIsTextLong] = useState(false)
  const [reducedHeight, setReducedHeight] = useState<number | string>()

  useEffect(() => {
    const handleResize = () => {
      if (textWrapperRef.current && (!fitParentHeight || initialLoad.current)) {
        if (initialLoad.current) {
          initialLoad.current = false
        }
        const textWrapperHeight =
          textWrapperRef.current.getBoundingClientRect().height
        const convertedCollapesdHeight = convertHeightToRem(collapsedHeight)
        const convertedTextWrapperHeight = convertHeightToRem(textWrapperHeight)
        setIsTextLong(convertedCollapesdHeight < convertedTextWrapperHeight)
        if (convertedTextWrapperHeight < convertedCollapesdHeight) {
          setReducedHeight(textWrapperHeight)
        } else {
          setReducedHeight(collapsedHeight)
        }
      }
      if (
        reducedHeight !== collapsedHeight &&
        fitParentHeight &&
        textWrapperRef.current
      ) {
        const textWrapperHeight =
          textWrapperRef.current.getBoundingClientRect().height
        const convertedTextWrapperHeight = convertHeightToRem(textWrapperHeight)
        setIsTextLong(
          convertedTextWrapperHeight > convertHeightToRem(reducedHeight)
        )
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [collapsedHeight, fitParentHeight, isTextLong, reducedHeight, children])

  useEffect(() => {
    if (
      fitParentHeight &&
      boxRef.current &&
      wrapperRef.current &&
      textWrapperRef.current
    ) {
      const boxHeight = boxRef.current.getBoundingClientRect().height
      const wrapperHeight = wrapperRef.current.getBoundingClientRect().height
      const textWrapperHeight =
        textWrapperRef.current.getBoundingClientRect().height
      const convertedBoxHeight = convertHeightToRem(boxHeight)
      const convertedWrapperHeight = convertHeightToRem(wrapperHeight)
      const convertedTextWrapperHeight = convertHeightToRem(textWrapperHeight)
      const isLong =
        convertedTextWrapperHeight > convertHeightToRem(reducedHeight)
      if (
        (convertedWrapperHeight > convertedBoxHeight && isLong) ||
        (convertHeightToRem(collapsedHeight) ===
          convertHeightToRem(reducedHeight) &&
          isLong)
      ) {
        setReducedHeight(wrapperHeight)
      }
    }
  }, [reducedHeight, collapsedHeight, fitParentHeight, wrapperRef])

  return (
    <Flex {...props} flexDirection="column" ref={boxRef}>
      <Wrapper position="relative" height={isExpanded ? '100%' : reducedHeight}>
        <Flex ref={textWrapperRef}>{children}</Flex>
        <Gradient
          isVisible={!isExpanded && isTextLong}
          position="absolute"
          bottom={0}
          width="100%"
          height={GRADIENT_HEIGHT}
        />
      </Wrapper>
      {(fitParentHeight || isTextLong) && (
        <ActionButtonWrapper
          isVisible={isTextLong}
          {...actionButtonWrapperProps}
        >
          {actionButton()}
        </ActionButtonWrapper>
      )}
    </Flex>
  )
}
